import { useQuery } from "react-query";
import apiService from "../services/apiService";
import { Language } from "../config/languages";
import cloneDeep from "lodash/cloneDeep";
import {
  FaqArticle,
  FaqCategory,
  FaqData,
  FaqSearchResult,
  FaqTag
} from "../types/Faq";
import { faqBaseUrl } from "../config/urls";
import { useMemo } from "react";
import { useLang } from "./LangHooks";
import find from "lodash/find";
import { useAlgoliaFaqArticleSearch } from "../services/algoliaService";

function enhanceFaqData(_data: FaqData): FaqData {
  const data = cloneDeep(_data);
  data.articles.sort(
    (a, b) =>
      100 * (b.promoted_article ? 1 : 0) -
      100 * (a.promoted_article ? 1 : 0) +
      a.title.localeCompare(b.title) -
      b.title.localeCompare(a.title)
  );
  return data;
}

export function useFaqData(lang: Language): FaqData {
  const { data } = useQuery<FaqData | undefined>(
    ["faqData", lang],
    async () => {
      try {
        return await apiService.loadFaqData(lang).then(enhanceFaqData);
      } catch (e) {
        console.error(e);
        return undefined;
      }
    }
  );
  return (
    data || {
      articles: [],
      categories: [],
      sitesWithFaq: [],
      tags: []
    }
  );
}

export function populateFaqSearchResult(
  faqData: FaqData,
  faqArticles: string[]
): FaqSearchResult[] {
  return faqArticles
    .map(_id => find(faqData.articles, { _id }))
    .filter((article): article is FaqArticle => !!article)
    .map(article => ({
      article,
      category: getFaqCategoriesForArticle(faqData, article)?.[0]
    }))
    .filter(({ category }) => !!category);
}

export function useFaqSearch(queryStr: string, limit = 10): FaqSearchResult[] {
  const lang = useLang();
  const faqData = useFaqData(lang);
  const { faqArticles } = useAlgoliaFaqArticleSearch(queryStr, limit);
  return useMemo(() => {
    return populateFaqSearchResult(faqData, faqArticles);
  }, [faqData, faqArticles]);
}

export function faqUrl(
  {
    category,
    tag,
    article
  }: {
    category?: FaqCategory;
    tag?: FaqTag;
    article?: FaqArticle;
  },
  onlyIds = false
) {
  if (!category) return faqBaseUrl;
  const url = `${faqBaseUrl}/${onlyIds ? category.id : category.name_slug}`;
  if (tag) {
    return `${url}/${tag.id}${onlyIds ? "" : "-" + tag.name_slug}`;
  }
  if (article) {
    return `${url}/q/${article.id}${onlyIds ? "" : "-" + article.title_slug}`;
  }
  return url;
}

export function getFaqCategoryById(
  faqData: FaqData,
  id?: string
): FaqCategory | undefined {
  return id
    ? find<FaqCategory>(faqData.categories, { id } as object)
    : undefined;
}

export function getFaqCategoryByNameSlug(
  faqData: FaqData,
  name_slug?: string
): FaqCategory | undefined {
  return name_slug
    ? find<FaqCategory>(faqData.categories, { name_slug } as object)
    : undefined;
}

export function getFaqCategoryByLegacyId(
  faqData: FaqData,
  legacyId?: string
): FaqCategory | undefined {
  return legacyId
    ? faqData.categories.filter(
        category => (category.legacyIds || []).indexOf(legacyId) >= 0
      )?.[0]
    : undefined;
}

export function getFaqTagById(
  faqData: FaqData,
  id?: string
): FaqTag | undefined {
  return id ? find<FaqTag>(faqData.tags, { id } as object) : undefined;
}

export function getFaqTagByLegacyName(
  faqData: FaqData,
  legacyName?: string
): FaqTag | undefined {
  return legacyName
    ? find<FaqTag>(faqData.tags, { legacyName } as object)
    : undefined;
}

export function getFaqArticleById(
  faqData: FaqData,
  id?: string
): FaqArticle | undefined {
  return id
    ? find<FaqArticle>(faqData.articles, {
        id
      } as object)
    : undefined;
}

export function getFaqArticleByLegacyId(
  faqData: FaqData,
  legacyId?: string
): FaqArticle | undefined {
  return legacyId
    ? faqData.articles.filter(
        article => (article.legacyIds || []).indexOf(legacyId) >= 0
      )?.[0]
    : undefined;
}

export function articleHasCategory(
  article: FaqArticle,
  category?: FaqCategory
): boolean {
  return category
    ? (article.categories || []).indexOf(category._id) >= 0
    : false;
}

export function getFaqCategoriesForArticle(
  faqData: FaqData,
  article: FaqArticle
): FaqCategory[] {
  return (article.categories || [])
    .map(category => {
      return find<FaqCategory>(faqData.categories, {
        _id: category
      } as object);
    })
    .filter((category): category is FaqCategory => !!category);
}
